import { useCallback, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { DialogExcluirComprador } from '@/features/proposta/components/etapa-comprador/dialog-excluir-comprador'
import { obterTodosCompradoresPFEPJ } from '@/features/proposta/entities/Comprador/merge-comprador-pj-pf'
import {
  type FieldArrayDeleteCompradorData,
  type FieldArrayDeleteCompradorActions,
  type ClienteParaExclusao,
  ExcluirCompradorValidator,
} from '@/features/proposta/entities/Comprador/validator/excluir-comprador-validator'
import type { TProcurador } from '@/features/proposta/interfaces/compradores.types'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TProcuradorPropostaSchema } from '@/features/proposta/schemas'
import { showToast } from '@/infra/sonner'

export const useExcluirComprador = (data: FieldArrayDeleteCompradorData, actions: FieldArrayDeleteCompradorActions) => {
  const shouldCleanupRef = useRef(false)
  const { t } = useTranslation('proposta')

  const obterProcuradoresDoComprador = useCallback(
    (id: string): TProcurador[] => {
      return data.procuradores
        .filter((procurador) => procurador.clientesIds.includes(id))
        .map((procurador) => {
          const cliente = data.clientes.find((_cliente) => _cliente.id === procurador.procuradorId)
          return { id: procurador.procuradorId, nome: cliente!.nome, cpf: cliente!.cpf, tipo: 'Procurador' }
        })
    },
    [data.clientes, data.procuradores],
  )

  const obterListaClientesParaExclusao = useCallback(
    (comprador: TCompradoresCadastrados[number]) => {
      const vinculos = comprador.vinculos ?? []
      const procuradoresDoComprador = obterProcuradoresDoComprador(comprador.id)
      const procuradoresDosVinculos = (comprador.vinculos ?? []).flatMap((vinculo) =>
        obterProcuradoresDoComprador(vinculo.id),
      )

      const listaClientesParaExclusao = [
        { id: comprador.id, nome: comprador.nome, tipo: 'Comprador' as const },
        ...vinculos.map(({ id, nome, tipo }) => ({ id, nome, tipo })),
        ...procuradoresDoComprador.map(({ id, nome }) => ({ id, nome, tipo: 'Procurador' as const })),
        ...procuradoresDosVinculos.map(({ id, nome }) => ({ id, nome, tipo: 'Procurador' as const })),
      ]

      return Array.from(new Map(listaClientesParaExclusao.map((cliente) => [cliente.id, cliente])).values()).sort(
        (a, b) => a.nome.localeCompare(b.nome),
      )
    },
    [obterProcuradoresDoComprador],
  )

  const obterIdsRelacionados = useCallback(
    (compradorId: string) => {
      const compradorPrincipal = data.compradores.find((c) => c.clienteId === compradorId)
      return new Set([compradorId, ...(compradorPrincipal?.vinculos?.map((v) => v.clienteId) ?? [])])
    },
    [data.compradores],
  )

  const atualizarProcurador = useCallback(
    (procurador: TProcuradorPropostaSchema, index: number, idsParaRemover: Set<string>) => {
      const clientesIdsRestantes = procurador.clientesIds.filter((id: string) => !idsParaRemover.has(id))

      if (clientesIdsRestantes.length === 0) {
        actions.removeProcurador(index)
      } else if (clientesIdsRestantes.length !== procurador.clientesIds.length) {
        actions.updateProcurador(index, { ...procurador, clientesIds: clientesIdsRestantes })
      }
    },
    [actions],
  )

  const excluirProcurador = useCallback(
    (procuradorId: string, compradorId: string) => {
      const procurador = data.procuradores.find((proc) => proc.procuradorId === procuradorId)
      if (!procurador) return

      const procuradorIndex = data.procuradores.findIndex((proc) => proc.procuradorId === procuradorId)
      const idsRelacionados = obterIdsRelacionados(compradorId)

      atualizarProcurador(procurador, procuradorIndex, idsRelacionados)
    },
    [data.procuradores, obterIdsRelacionados, atualizarProcurador],
  )

  const excluirProcuradorDoCompradorEVinculos = useCallback(
    (compradorId: string) => {
      const idsRelacionados = obterIdsRelacionados(compradorId)

      data.procuradores.forEach((procurador, index) => {
        atualizarProcurador(procurador, index, idsRelacionados)
      })
    },
    [data.procuradores, obterIdsRelacionados, atualizarProcurador],
  )

  const excluirComprador = useCallback(
    (compradorId: string) => {
      const compradorIndex = data.compradores.findIndex((comprador) => comprador.clienteId === compradorId)
      if (compradorIndex !== -1) actions.removeComprador(compradorIndex)

      excluirProcuradorDoCompradorEVinculos(compradorId)
    },
    [actions, data.compradores, excluirProcuradorDoCompradorEVinculos],
  )

  const excluirVinculoComprador = useCallback(
    (id: string, compradorId: string) => {
      const comprador = data.compradores.find(
        (c) => c.clienteId === compradorId && c.vinculos.some((v) => v.clienteId === id),
      )
      if (!comprador) return
      const compradorIndex = data.compradores.findIndex((c) => c.clienteId === comprador.clienteId)

      const vinculosAtualizados = comprador.vinculos.filter((vinculo) => vinculo.clienteId !== id) ?? []
      actions.updateComprador(compradorIndex, { ...comprador, vinculos: vinculosAtualizados })
    },
    [actions, data.compradores],
  )

  const excluirCliente = useCallback(
    (cliente: ClienteParaExclusao, compradorId: string) => {
      switch (cliente.tipo) {
        case 'Comprador':
          excluirComprador(cliente.id)
          break
        case 'Procurador':
          excluirProcurador(cliente.id, compradorId)
          break
        default:
          excluirVinculoComprador(cliente.id, compradorId)
          break
      }
    },
    [excluirComprador, excluirVinculoComprador, excluirProcurador],
  )

  const handleExcluirCliente = useCallback(
    (cliente: ClienteParaExclusao, compradorId: string) => {
      const mensagemErro = ExcluirCompradorValidator.validar(cliente, compradorId, data)

      if (mensagemErro) {
        showToast({ message: mensagemErro, variant: 'error' })
        return
      }

      excluirCliente(cliente, compradorId)
      shouldCleanupRef.current = true
      showToast({ message: t('resumo_proposta.compradores.toast.exclusão'), variant: 'success' })
    },
    [data, excluirCliente, t],
  )

  const handleOnExcluirComprador = useCallback(
    async (compradorId: string) => {
      const compradoresComum = obterTodosCompradoresPFEPJ(data.compradores, data.clientes, data.clientesJuridicos)
      const comprador = compradoresComum.find((c) => c.id === compradorId)
      if (!comprador) return

      const listaClientesParaExclusao = obterListaClientesParaExclusao(comprador)

      const result = await DialogExcluirComprador.show({ clientes: listaClientesParaExclusao })

      if (result.value === 'confirm') {
        const clienteSelecionado = listaClientesParaExclusao.find(
          (item) => item.id === result.data?.clienteSelecionadoId,
        )
        handleExcluirCliente(clienteSelecionado!, compradorId)
      }
    },
    [data.clientes, data.clientesJuridicos, data.compradores, handleExcluirCliente, obterListaClientesParaExclusao],
  )

  const excluirClientesNaoReferenciados = useCallback(() => {
    const compradoresAtuais = data.compradores
    const procuradoresAtuais = data.procuradores

    const clientesReferenciados = new Set([
      ...compradoresAtuais.flatMap((c) => [c.clienteId, ...(c.vinculos?.map((v) => v.clienteId) ?? [])]),
      ...procuradoresAtuais.map((p) => p.procuradorId),
    ])

    data.clientes
      .map((cliente, index) => ({ cliente, index }))
      .filter(({ cliente }) => !clientesReferenciados.has(cliente.id))
      .reverse()
      .forEach(({ index }) => actions.removeCliente(index))

    data.clientesJuridicos
      .map((cliente, index) => ({ cliente, index }))
      .filter(({ cliente }) => !clientesReferenciados.has(cliente.id))
      .reverse()
      .forEach(({ index }) => actions.removeClienteJuridico(index))
  }, [actions, data.clientes, data.clientesJuridicos, data.compradores, data.procuradores])

  useEffect(() => {
    if (shouldCleanupRef.current) {
      shouldCleanupRef.current = false
      excluirClientesNaoReferenciados()
    }
  }, [data.procuradores, data.compradores, excluirClientesNaoReferenciados])

  return { handleOnExcluirComprador, obterListaClientesParaExclusao, obterProcuradoresDoComprador }
}
